import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../apis/axiosConfig";
import { useDarkMode } from "../../hooks/useDarkMode";

const SeoResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId, projectName, url, keyword, endpoint } =
    location.state || {};
  const { isDarkMode } = useDarkMode();

  const [loading, setLoading] = useState(true);
  const [seoData, setSeoData] = useState(null);
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});
  const [useDemoData, setUseDemoData] = useState(false);
  const [usingFullFallback, setUsingFullFallback] = useState(false);

  // Back navigation helper: return to previous page if possible, else Dashboard
  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/dashboard");
    }
  };

  const loadingMessages = [
    "🚀 Analyzing your website...",
    "⏳ Great things take time...",
    "🔍 Scanning SEO metrics...",
    "📊 Collecting data...",
    "💡 Processing insights...",
    "🎯 Almost there...",
  ];

  useEffect(() => {
    if (!projectId) {
      navigate("/dashboard");
      return;
    }

    // Cycle through loading messages
    const messageInterval = setInterval(() => {
      setLoadingMessage((prev) => (prev + 1) % loadingMessages.length);
    }, 3000);

    // Poll for SEO data (project-wide or endpoint-specific)
    let pollCount = 0;
    const maxPolls = 60; // 5 minutes with 5-second intervals
    let pollInterval = null; // declare upfront to avoid TDZ issues

    const pollData = async () => {
      try {
        let response;
        if (endpoint && !usingFullFallback) {
          // Endpoint-specific results
          const encoded = encodeURIComponent(endpoint.replace(/^\//, ""));
          response = await axiosInstance.get(
            `/project/${projectId}/seo/${encoded}/`,
          );
        } else {
          // Full project results
          response = await axiosInstance.get(
            `/project/${projectId}/seo-results/`,
          );
        }

        // Accept different shapes for endpoint vs full results
        const data =
          response.data?.results || response.data?.data || response.data;
        if (data) {
          setSeoData(data);
          setLoading(false);
          clearInterval(messageInterval);
          if (pollInterval) clearInterval(pollInterval);
        } else {
          pollCount++;
          if (pollCount >= maxPolls) {
            setError(
              "SEO analysis is taking longer than expected. Please check back later.",
            );
            setLoading(false);
            clearInterval(messageInterval);
            if (pollInterval) clearInterval(pollInterval);
          }
        }
      } catch (err) {
        // console.error('Error fetching SEO data:', err);

        // Check if it's a 404 - not found or not generated
        if (err.response?.status === 404) {
          // If endpoint-specific isn't available, fall back to full results immediately
          if (endpoint && !usingFullFallback) {
            try {
              setUsingFullFallback(true);
              const fullResp = await axiosInstance.get(
                `/project/${projectId}/seo-results/`,
              );
              const data =
                fullResp.data?.results || fullResp.data?.data || fullResp.data;
              if (data) {
                setSeoData(data);
                setLoading(false);
                clearInterval(messageInterval);
                if (pollInterval) clearInterval(pollInterval);
                return;
              }
            } catch (inner) {
              // if even full results are not present, continue polling until timeout
            }
          }

          pollCount++;
          if (pollCount >= maxPolls) {
            setError(
              "SEO analysis is still in progress or not started. Please wait a few minutes and try again from the dashboard.",
            );
            setLoading(false);
            clearInterval(messageInterval);
            if (pollInterval) clearInterval(pollInterval);
          }
        } else {
          // For other errors, stop immediately
          setError(
            err.response?.data?.message ||
              err.response?.data?.error ||
              "Failed to fetch SEO data. Please try again.",
          );
          setLoading(false);
          clearInterval(messageInterval);
          if (pollInterval) clearInterval(pollInterval);
        }
      }
    };

    // Initial fetch
    pollData();

    // Poll every 5 seconds
    pollInterval = setInterval(pollData, 5000);

    return () => {
      clearInterval(messageInterval);
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [projectId, endpoint, navigate]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const renderSectionData = (data, title) => {
    if (!data) return null;

    // Handle error status
    if (data.status === "error") {
      return (
        <div
          className={`border rounded-lg p-4 ${isDarkMode ? "bg-red-900/30 border-red-700" : "bg-red-50 border-red-200"}`}
        >
          <p className={isDarkMode ? "text-red-300" : "text-red-700"}>
            ❌ {data["error message"] || "Error occurred"}
          </p>
        </div>
      );
    }

    // Handle successful data
    if (data.status === "ok" && data.data) {
      return (
        <div
          className={`border rounded-lg p-4 ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-white border-gray-200"}`}
        >
          <pre
            className={`text-sm whitespace-pre-wrap overflow-auto max-h-96 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            {JSON.stringify(data.data, null, 2)}
          </pre>
        </div>
      );
    }

    return (
      <div
        className={`border rounded-lg p-4 ${isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200"}`}
      >
        <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
          No data available
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-6 ${isDarkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" : "bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50"}`}
      >
        <div className="text-center">
          {/* Animated loader */}
          <div className="mb-8 relative">
            <div className="w-32 h-32 mx-auto relative">
              {/* Outer rotating circle */}
              <div
                className={`absolute inset-0 border-8 rounded-full animate-spin ${isDarkMode ? "border-blue-900" : "border-blue-200"}`}
                style={{ borderTopColor: "#3b82f6" }}
              ></div>
              {/* Inner pulsing circle */}
              <div className="absolute inset-4 bg-blue-500 rounded-full animate-pulse flex items-center justify-center">
                <span className="text-4xl">🔍</span>
              </div>
            </div>
          </div>

          {/* Loading message */}
          <h2
            className={`text-3xl font-bold mb-4 animate-fade-in ${isDarkMode ? "text-gray-100" : "text-gray-800"}`}
          >
            {loadingMessages[loadingMessage]}
          </h2>

          {/* Project info */}
          <div
            className={`backdrop-blur rounded-xl shadow-lg p-6 max-w-md mx-auto mb-6 ${isDarkMode ? "bg-gray-800/80" : "bg-white/80"}`}
          >
            <h3
              className={`font-semibold mb-3 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              Analyzing
            </h3>
            <p className="text-lg font-bold text-blue-600 mb-2">
              {projectName}
            </p>
            <p
              className={`text-sm break-all mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              🔗 {url}
            </p>
            {endpoint && (
              <p
                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
              >
                Endpoint: <span className="font-mono">{endpoint}</span>
              </p>
            )}
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              🎯 Keyword: {keyword}
            </p>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2">
            <div
              className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
              style={{ animationDelay: "0s" }}
            ></div>
            <div
              className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.2s" }}
            ></div>
            <div
              className="w-3 h-3 bg-blue-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.4s" }}
            ></div>
          </div>

          <p
            className={`mt-6 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            This may take a few minutes...
          </p>

          {/* Demo Mode Button - for testing when API is not ready */}
          <button
            onClick={() => {
              setUseDemoData(true);
              // Load demo data from the media folder example
              fetch("/media/novarsis_seo_data.json")
                .then((res) => res.json())
                .then((data) => {
                  setSeoData(data);
                  setLoading(false);
                })
                .catch(() => {
                  setError("Could not load demo data");
                  setLoading(false);
                });
            }}
            className="mt-6 bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition text-sm"
          >
            📊 Load Demo Data (For Testing)
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <div
          className={`rounded-xl shadow-lg p-8 max-w-md text-center ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p
            className={`mb-6 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            {error}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={goBack}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition"
            >
              ← Back
            </button>
            <button
              onClick={() => {
                setError(null);
                setLoading(true);
                // Try loading demo data
                fetch("/media/novarsis_seo_data.json")
                  .then((res) => res.json())
                  .then((data) => {
                    setSeoData(data);
                    setLoading(false);
                  })
                  .catch(() => {
                    setError(
                      "Could not load demo data. Please go back to dashboard.",
                    );
                    setLoading(false);
                  });
              }}
              className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition"
            >
              📊 View Demo Data Instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Organize sections
  const sections = [
    {
      key: "seo_content_analysis",
      title: "📝 SEO Content Analysis",
      icon: "📝",
    },
    { key: "technical_audit", title: "🔧 Technical Audit", icon: "🔧" },
    { key: "domain_authority", title: "🏆 Domain Authority", icon: "🏆" },
    { key: "backlink_summary", title: "🔗 Backlink Summary", icon: "🔗" },
    { key: "backlinks", title: "🌐 Backlinks", icon: "🌐" },
    { key: "lost_backlinks", title: "💔 Lost Backlinks", icon: "💔" },
    { key: "readability", title: "📖 Readability", icon: "📖" },
    { key: "internal_links", title: "🔄 Internal Links", icon: "🔄" },
    { key: "domain_age", title: "🕰️ Domain Age", icon: "🕰️" },
    { key: "canonical", title: "🎯 Canonical Tags", icon: "🎯" },
    { key: "robots_txt", title: "🤖 Robots.txt", icon: "🤖" },
    { key: "meta_description", title: "📋 Meta Description", icon: "📋" },
    { key: "headings", title: "📑 Headings", icon: "📑" },
    { key: "keyword_density", title: "🎯 Keyword Density", icon: "🎯" },
    { key: "title_tag", title: "🏷️ Title Tag", icon: "🏷️" },
    { key: "word_count", title: "📊 Word Count", icon: "📊" },
    { key: "helpful_content", title: "💡 Helpful Content", icon: "💡" },
    { key: "pwa_audit", title: "📱 PWA Audit", icon: "📱" },
    { key: "website_traffic", title: "📈 Website Traffic", icon: "📈" },
    { key: "website_traffic_history", title: "📊 Traffic History", icon: "📊" },
    { key: "keyword_difficulty", title: "💪 Keyword Difficulty", icon: "💪" },
    { key: "keyword_ideas", title: "💭 Keyword Ideas", icon: "💭" },
    { key: "keyword_suggestions", title: "🔑 Keyword Suggestions", icon: "🔑" },
    { key: "related_keywords", title: "🔗 Related Keywords", icon: "🔗" },
    { key: "competitor_analysis", title: "🎯 Competitor Analysis", icon: "🎯" },
    { key: "competitor_keywords", title: "🔍 Competitor Keywords", icon: "🔍" },
    { key: "serp_data", title: "🔍 SERP Data", icon: "🔍" },
    { key: "serp_analyzer", title: "📊 SERP Analyzer", icon: "📊" },
    { key: "chatgpt_visibility", title: "🤖 ChatGPT Visibility", icon: "🤖" },
    { key: "gemini_visibility", title: "💎 Gemini Visibility", icon: "💎" },
    {
      key: "perplexity_visibility",
      title: "🌟 Perplexity Visibility",
      icon: "🌟",
    },
    { key: "plagiarism_check", title: "📄 Plagiarism Check", icon: "📄" },
    { key: "content_generator", title: "✍️ Content Generator", icon: "✍️" },
    { key: "news_discover", title: "📰 News Discover", icon: "📰" },
    { key: "ai_analysis", title: "🤖 AI Analysis", icon: "🤖" },
  ];

  return (
    <div
      className={`min-h-screen py-8 px-4 ${isDarkMode ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" : "bg-gradient-to-br from-blue-50 via-white to-purple-50"}`}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          className={`rounded-xl shadow-lg p-6 mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h1
                className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-800"}`}
              >
                SEO Analysis Results
              </h1>
              {useDemoData && (
                <span className="bg-purple-100 text-purple-700 text-xs px-3 py-1 rounded-full font-semibold">
                  📊 Demo Data
                </span>
              )}
              {endpoint && usingFullFallback && (
                <span className="ml-2 bg-amber-100 text-amber-700 text-xs px-3 py-1 rounded-full font-semibold">
                  Fallback: full project
                </span>
              )}
            </div>
            <button
              onClick={goBack}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              ← Back
            </button>
          </div>
          <div
            className={`border-t pt-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <p className="text-lg font-semibold text-blue-600 mb-2">
              {projectName}
            </p>
            <p
              className={`text-sm break-all mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              🔗 {url}
            </p>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
            >
              🎯 Keyword: {keyword}
            </p>
            <p
              className={`text-xs mt-2 ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
            >
              Generated:{" "}
              {seoData?.timestamp
                ? new Date(seoData.timestamp * 1000).toLocaleString()
                : new Date().toLocaleString()}
            </p>
          </div>

          {/* Summary Stats */}
          {seoData && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div
                className={`rounded-lg p-4 text-center border ${isDarkMode ? "bg-green-900/30 border-green-700" : "bg-green-50 border-green-200"}`}
              >
                <div className="text-3xl font-bold text-green-600">
                  {seoData.successful_its || 0}
                </div>
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  Successful Checks
                </p>
              </div>
              <div
                className={`rounded-lg p-4 text-center border ${isDarkMode ? "bg-red-900/30 border-red-700" : "bg-red-50 border-red-200"}`}
              >
                <div className="text-3xl font-bold text-red-600">
                  {seoData.failed_apis || 0}
                </div>
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  Failed Checks
                </p>
              </div>
              <div
                className={`rounded-lg p-4 text-center border ${isDarkMode ? "bg-blue-900/30 border-blue-700" : "bg-blue-50 border-blue-200"}`}
              >
                <div className="text-3xl font-bold text-blue-600">
                  {seoData.total_apis || 0}
                </div>
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  Total APIs
                </p>
              </div>
            </div>
          )}
        </div>

        {/* SEO Data Sections */}
        <div className="space-y-4">
          {sections.map((section) => {
            const sectionData = seoData?.[section.key];
            if (!sectionData) return null;

            const isExpanded = expandedSections[section.key];
            const hasError = sectionData.status === "error";

            return (
              <div
                key={section.key}
                className={`rounded-xl shadow-md overflow-hidden transition-all ${
                  hasError
                    ? "border-l-4 border-red-500"
                    : "border-l-4 border-blue-500"
                } ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
              >
                <button
                  onClick={() => toggleSection(section.key)}
                  className={`w-full flex items-center justify-between p-5 transition ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{section.icon}</span>
                    <h3
                      className={`text-lg font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                    >
                      {section.title}
                    </h3>
                    {hasError && (
                      <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full">
                        Error
                      </span>
                    )}
                    {sectionData.status === "ok" && (
                      <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                        ✓ OK
                      </span>
                    )}
                  </div>
                  <svg
                    className={`w-6 h-6 transition-transform ${
                      isExpanded ? "rotate-180" : ""
                    } ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {isExpanded && (
                  <div
                    className={`p-5 border-t ${isDarkMode ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                  >
                    {renderSectionData(sectionData, section.title)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SeoResult;
